import { format } from 'date-fns';
import React from 'react';
import { View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { ReservationType } from '@/api/partners/reservations/types';
import type {
  AdminReservation,
  DailyHostEarningsBreakdown,
  HostEarningsBreakdown,
  Insurance,
  SubscriptionHostEarningsBreakdown,
} from '@/api/reservations/types';
import { isDailyHostEarningsBreakdown } from '@/api/reservations/types';
import { Text } from '@/ui';
import { Loader } from '@/ui/loader';

type AtAGlance = {
  customerName: string | null;
  customerPhone: string | null;
  pickupDatetime: Date | null;
  pickupAddress: string | null;
  returnDatetime: Date | null;
  returnAddress: string | null;
};

type Props = {
  glance: AtAGlance;
  quote: AdminReservation | undefined;
  isQuoting: boolean;
  quoteError?: string | null;
  missingRequirements?: string[];
  insuranceName?: string | null;
  reservationType: ReservationType;
  pricingCaption?: string | null;
};

const formatMissingRequirements = (items: string[]) => {
  if (items.length <= 1) {
    return items.join('');
  }

  if (items.length === 2) {
    return `${items[0]} and ${items[1]}`;
  }

  return `${items.slice(0, -1).join(', ')}, and ${items.at(-1)}`;
};

const money = (value?: number | null) =>
  value == null || Number.isNaN(value) ? '-' : `$${value.toFixed(2)}`;

const discountAmount = (value?: number | null) =>
  value == null || value <= 0 ? '-' : `-$${value.toFixed(2)}`;

const roundMoney = (value: number) => Math.round(value * 100) / 100;

const customerRateDiscount = (
  quote: AdminReservation,
  discountType: 'Weekly Discount' | 'Monthly Discount'
) =>
  (quote.reservationRates ?? [])
    .filter((rate) => rate.discountType === discountType)
    .reduce((sum, rate) => sum + rate.discountAmount, 0);

const customerManualDiscount = (quote: AdminReservation) =>
  (quote.manualDiscount ?? 0) + (quote.ownerDiscount ?? 0);

const grossDailyRentalEarnings = (breakdown: DailyHostEarningsBreakdown) =>
  roundMoney(
    breakdown.baseRental * ((breakdown.ownerEarningsPercent ?? 0) / 100)
  );

const dailyRateDiscountEarningsTotal = (
  breakdown: DailyHostEarningsBreakdown
) => roundMoney(grossDailyRentalEarnings(breakdown) - breakdown.rentalEarnings);

const ownerRateDiscountEarnings = (
  breakdown: DailyHostEarningsBreakdown,
  quote: AdminReservation,
  discountType: 'Weekly Discount' | 'Monthly Discount'
): number => {
  const hasWeekly =
    (breakdown.weeklyDiscount ?? 0) > 0 ||
    customerRateDiscount(quote, 'Weekly Discount') > 0;
  const hasMonthly =
    (breakdown.monthlyDiscount ?? 0) > 0 ||
    customerRateDiscount(quote, 'Monthly Discount') > 0;
  const total = dailyRateDiscountEarningsTotal(breakdown);

  if (discountType === 'Weekly Discount') {
    if (!hasWeekly) {
      return 0;
    }

    if (!hasMonthly) {
      return total;
    }

    const customerWeekly = customerRateDiscount(quote, 'Weekly Discount');
    const customerMonthly = customerRateDiscount(quote, 'Monthly Discount');
    const customerTotal = customerWeekly + customerMonthly;

    if (customerTotal <= 0) {
      return total;
    }

    return roundMoney(total * (customerWeekly / customerTotal));
  }

  if (!hasMonthly) {
    return 0;
  }

  if (!hasWeekly) {
    return total;
  }

  const customerWeekly = customerRateDiscount(quote, 'Weekly Discount');
  const customerMonthly = customerRateDiscount(quote, 'Monthly Discount');
  const customerTotal = customerWeekly + customerMonthly;

  if (customerTotal <= 0) {
    return 0;
  }

  const weeklyDisplay = roundMoney(total * (customerWeekly / customerTotal));

  return roundMoney(total - weeklyDisplay);
};

const rateSubtotal = (quote: AdminReservation) =>
  (quote.reservationRates ?? []).reduce(
    (sum, rate) => sum + rate.dailyRate * rate.numDays,
    0
  );

const subscriptionCustomerPrice = (quote: AdminReservation) => {
  const fromRates = rateSubtotal(quote);
  if (fromRates > 0) {
    return fromRates;
  }

  const insurance = quote.insuranceCost ?? 0;
  const delivery = quote.totalDeliveryCost ?? 0;
  return quote.totalCost - insurance - delivery;
};

const formatMileageTier = (tier?: number | 'Unlimited' | null) => {
  if (tier == null) {
    return '';
  }

  return tier === 'Unlimited' ? 'Unlimited miles' : `${tier} miles`;
};

const getInsuranceTitle = (
  insurance: Insurance,
  insuranceName?: string | null
) => {
  if (insurance.category?.trim()) {
    return insurance.category.trim();
  }

  if (insuranceName?.trim()) {
    return insuranceName.trim();
  }

  if (insurance.minidescription?.trim()) {
    return insurance.minidescription.trim();
  }

  if (insurance.description?.trim()) {
    return insurance.description.trim();
  }

  const tag = insurance.tag?.trim();
  if (tag && tag.toLowerCase() !== 'customer') {
    return tag.charAt(0).toUpperCase() + tag.slice(1);
  }

  return 'Insurance';
};

const formatRenewalPeriod = (period?: string | null) => {
  if (!period) {
    return '';
  }

  if (period === '6_months') {
    return '6 months';
  }

  return period.charAt(0).toUpperCase() + period.slice(1);
};

const GlanceItem = ({
  label,
  lines,
}: {
  label: string;
  lines: (string | null)[];
}) => {
  const { styles } = useStyles(stylesheet);
  const visibleLines = lines.filter(Boolean) as string[];

  if (visibleLines.length === 0) {
    return null;
  }

  return (
    <View style={{ gap: 4 }}>
      <Text type="caption1" style={styles.secondaryText}>
        {label}
      </Text>

      {visibleLines.map((line) => (
        <Text key={line} type="bodyRegular">
          {line}
        </Text>
      ))}
    </View>
  );
};

const AmountPair = ({
  customerPrice,
  hostEarnings,
  discount = false,
  bold = false,
  customerOnly = false,
}: {
  customerPrice?: string;
  hostEarnings?: string;
  discount?: boolean;
  bold?: boolean;
  customerOnly?: boolean;
}) => {
  const { styles } = useStyles(stylesheet);
  const customerType = bold ? 'bodyBold' : 'bodyRegular';
  const earningsType = bold ? 'bodyBold' : 'bodyRegular';

  return (
    <View
      style={[styles.amountPair, customerOnly && styles.amountPairCustomerOnly]}
    >
      {customerPrice ? (
        <Text
          type={customerType}
          style={[
            styles.customerAmount,
            discount ? styles.discountText : styles.secondaryText,
          ]}
        >
          {customerPrice}
        </Text>
      ) : (
        <View style={styles.customerAmountSlot} />
      )}

      {customerOnly ? null : (
        <Text
          type={earningsType}
          style={[
            styles.earningsAmount,
            discount ? styles.discountText : undefined,
          ]}
        >
          {hostEarnings ?? '-'}
        </Text>
      )}
    </View>
  );
};

const PaymentsHeader = ({
  customerOnly = false,
}: {
  customerOnly?: boolean;
}) => {
  const { styles } = useStyles(stylesheet);

  return (
    <View style={styles.paymentsHeader}>
      <Text type="title2" style={styles.paymentsTitle}>
        Payments
      </Text>

      <View style={styles.amountPair}>
        <Text
          type="caption1"
          numberOfLines={1}
          style={[
            styles.columnLabel,
            styles.customerColumn,
            styles.secondaryText,
          ]}
        >
          Customer price
        </Text>

        {customerOnly ? null : (
          <Text
            type="caption1"
            numberOfLines={1}
            style={[
              styles.columnLabel,
              styles.earningsColumn,
              styles.primaryText,
            ]}
          >
            Your earnings
          </Text>
        )}
      </View>
    </View>
  );
};

const RateLineItem = ({
  title,
  rateLabel,
  quantityLabel,
  customerPrice,
  hostEarnings,
  customerOnly = false,
}: {
  title: string;
  rateLabel?: string;
  quantityLabel?: string;
  customerPrice?: string;
  hostEarnings?: string;
  customerOnly?: boolean;
}) => {
  const { styles } = useStyles(stylesheet);

  return (
    <View style={styles.stackedLineItem}>
      <Text type="bodyRegular">{title}</Text>

      <View style={styles.rateLineMeta}>
        <View style={styles.rateMetaLeft}>
          {rateLabel ? (
            <Text
              type="bodyRegular"
              style={[styles.secondaryText, styles.rateLabel]}
            >
              {rateLabel}
            </Text>
          ) : null}

          {quantityLabel ? (
            <Text
              type="bodyRegular"
              style={[styles.secondaryText, styles.quantityLabel]}
            >
              {quantityLabel}
            </Text>
          ) : null}
        </View>

        <AmountPair
          customerPrice={customerPrice}
          hostEarnings={hostEarnings}
          customerOnly={customerOnly}
        />
      </View>
    </View>
  );
};

const CaptionLineItem = ({
  title,
  caption,
  customerPrice,
  hostEarnings,
  customerOnly = false,
}: {
  title: string;
  caption?: string;
  customerPrice?: string;
  hostEarnings?: string;
  customerOnly?: boolean;
}) => {
  const { styles } = useStyles(stylesheet);

  return (
    <View style={styles.stackedLineItem}>
      <Text type="bodyRegular">{title}</Text>

      <View style={styles.rateLineMeta}>
        {caption ? (
          <Text
            type="bodyRegular"
            style={[styles.secondaryText, styles.captionLabel]}
          >
            {caption}
          </Text>
        ) : (
          <View style={styles.captionLabel} />
        )}

        <AmountPair
          customerPrice={customerPrice}
          hostEarnings={hostEarnings}
          customerOnly={customerOnly}
        />
      </View>
    </View>
  );
};

const SimpleLineItem = ({
  title,
  customerPrice,
  hostEarnings,
  customerOnly = false,
}: {
  title: string;
  customerPrice?: string;
  hostEarnings?: string;
  discount?: boolean;
  customerOnly?: boolean;
}) => {
  const { styles } = useStyles(stylesheet);

  return (
    <View style={styles.simpleLineItem}>
      <Text type="bodyRegular" style={styles.simpleLineItemTitle}>
        {title}
      </Text>

      <AmountPair
        customerPrice={customerPrice}
        hostEarnings={hostEarnings}
        customerOnly={customerOnly}
      />
    </View>
  );
};

const DiscountLineItem = ({
  title,
  customerPrice,
  hostEarnings,
  customerOnly = false,
}: {
  title: string;
  customerPrice?: string;
  hostEarnings?: string;
  customerOnly?: boolean;
}) => {
  const { styles } = useStyles(stylesheet);

  return (
    <View style={styles.simpleLineItem}>
      <Text
        type="bodyRegular"
        style={[styles.simpleLineItemTitle, styles.discountText]}
      >
        {title}
      </Text>

      <AmountPair
        customerPrice={customerPrice}
        hostEarnings={hostEarnings}
        discount
        customerOnly={customerOnly}
      />
    </View>
  );
};

const PaymentsTotal = ({
  customerTotal,
  earningsTotal,
  customerOnly = false,
}: {
  customerTotal?: string;
  earningsTotal?: string;
  customerOnly?: boolean;
}) => {
  const { styles } = useStyles(stylesheet);

  return (
    <View style={styles.paymentsFooter}>
      <Text type="bodyBold">Total Cost</Text>

      <AmountPair
        customerPrice={customerTotal}
        hostEarnings={earningsTotal}
        bold
        customerOnly={customerOnly}
      />
    </View>
  );
};

const PaymentsCard = ({
  children,
  customerOnly = false,
}: {
  children: React.ReactNode;
  customerOnly?: boolean;
}) => {
  const { styles } = useStyles(stylesheet);

  return (
    <View style={styles.paymentsCard}>
      <PaymentsHeader customerOnly={customerOnly} />
      <View style={styles.paymentsBody}>{children}</View>
    </View>
  );
};

const renderDailyBreakdown = (
  quote: AdminReservation,
  breakdown: DailyHostEarningsBreakdown,
  insuranceName?: string | null
) => {
  const carName = quote.car
    ? `${quote.car.brandName} ${quote.car.modelName}`
    : 'Rental';

  const carSubtotal = rateSubtotal(quote);

  const extraEarnings = (amount: number) =>
    (Math.round(amount * (breakdown.ownerEarningsPercent ?? 0)) / 100).toFixed(
      2
    );

  const rate = quote.reservationRates?.[0];
  const dayCaption = rate ? `x${breakdown.numDays} days` : undefined;

  return (
    <>
      <RateLineItem
        title={carName}
        rateLabel={rate ? `${money(breakdown.dailyRate)}/day` : undefined}
        quantityLabel={dayCaption}
        customerPrice={money(carSubtotal)}
        hostEarnings={money(grossDailyRentalEarnings(breakdown))}
      />

      {quote.insurance ? (
        (quote.insuranceCost ?? 0) > 0 && quote.numDays > 0 ? (
          <RateLineItem
            title={getInsuranceTitle(quote.insurance, insuranceName)}
            rateLabel={`${money(quote.insuranceCost / quote.numDays)}/day`}
            quantityLabel={dayCaption}
            customerPrice={money(quote.insuranceCost)}
            hostEarnings="-"
          />
        ) : (
          <SimpleLineItem
            title={getInsuranceTitle(quote.insurance, insuranceName)}
            customerPrice={money(quote.insuranceCost)}
            hostEarnings="-"
          />
        )
      ) : null}

      {(quote.extras ?? []).map((extra) => (
        <SimpleLineItem
          key={extra.id}
          title={extra.title}
          customerPrice={money(extra.amount)}
          hostEarnings={`$${extraEarnings(extra.amount)}`}
        />
      ))}

      {(quote.totalDeliveryCost ?? 0) > 0 ? (
        <SimpleLineItem
          title="Delivery fee"
          customerPrice={money(quote.totalDeliveryCost)}
          hostEarnings={money(breakdown.delivery)}
        />
      ) : null}

      {(breakdown.charging ?? 0) > 0 ? (
        <SimpleLineItem
          title="Supercharging"
          hostEarnings={money(breakdown.charging)}
        />
      ) : null}

      {(breakdown.tolls ?? 0) > 0 ? (
        <SimpleLineItem title="Tolls" hostEarnings={money(breakdown.tolls)} />
      ) : null}

      {(breakdown.extraMiles ?? 0) > 0 ? (
        <SimpleLineItem
          title="Extra miles"
          hostEarnings={money(breakdown.extraMiles)}
        />
      ) : null}

      {(quote.salesTax ?? 0) > 0 ? (
        <SimpleLineItem
          title="Sales tax"
          customerPrice={money(quote.salesTax)}
          customerOnly
        />
      ) : null}

      {(breakdown.weeklyDiscount ?? 0) > 0 ||
      customerRateDiscount(quote, 'Weekly Discount') > 0 ? (
        <DiscountLineItem
          title="Weekly Discount"
          customerPrice={discountAmount(
            customerRateDiscount(quote, 'Weekly Discount')
          )}
          hostEarnings={discountAmount(
            ownerRateDiscountEarnings(breakdown, quote, 'Weekly Discount')
          )}
        />
      ) : null}

      {(breakdown.monthlyDiscount ?? 0) > 0 ||
      customerRateDiscount(quote, 'Monthly Discount') > 0 ? (
        <DiscountLineItem
          title="Monthly Discount"
          customerPrice={discountAmount(
            customerRateDiscount(quote, 'Monthly Discount')
          )}
          hostEarnings={discountAmount(
            ownerRateDiscountEarnings(breakdown, quote, 'Monthly Discount')
          )}
        />
      ) : null}

      {(breakdown.ownerDiscount ?? 0) > 0 ||
      customerManualDiscount(quote) > 0 ? (
        <DiscountLineItem
          title="Manual Discount"
          customerPrice={discountAmount(customerManualDiscount(quote))}
          hostEarnings={discountAmount(breakdown.ownerDiscount)}
        />
      ) : null}

      {(quote.holdAmount ?? 0) > 0 ? (
        <SimpleLineItem
          title="Security deposit"
          customerPrice={money(quote.holdAmount)}
          customerOnly
        />
      ) : null}
    </>
  );
};

const renderSubscriptionBreakdown = (
  quote: AdminReservation,
  breakdown: SubscriptionHostEarningsBreakdown,
  insuranceName?: string | null
) => {
  const carName = quote.car
    ? `${quote.car.brandName} ${quote.car.modelName}`
    : 'Subscription';

  const mileageLabel = formatMileageTier(breakdown.mileageTier);
  const caption = [formatRenewalPeriod(breakdown.renewalPeriod), mileageLabel]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <CaptionLineItem
        title={carName}
        caption={caption || undefined}
        customerPrice={money(subscriptionCustomerPrice(quote))}
        hostEarnings={money(breakdown.baseEarnings)}
      />

      {quote.insurance ? (
        <SimpleLineItem
          title={getInsuranceTitle(quote.insurance, insuranceName)}
          customerPrice={money(quote.insuranceCost)}
          hostEarnings="-"
        />
      ) : null}

      {(breakdown.delivery ?? 0) > 0 ? (
        <SimpleLineItem
          title="Delivery fee"
          customerPrice={money(quote.totalDeliveryCost)}
          hostEarnings={money(breakdown.delivery)}
        />
      ) : null}

      {(breakdown.charging ?? 0) > 0 ? (
        <SimpleLineItem
          title="Supercharging"
          hostEarnings={money(breakdown.charging)}
        />
      ) : null}

      {(breakdown.tolls ?? 0) > 0 ? (
        <SimpleLineItem title="Tolls" hostEarnings={money(breakdown.tolls)} />
      ) : null}

      {(quote.initiationFee ?? 0) > 0 ? (
        <SimpleLineItem
          title="Initiation fee"
          customerPrice={money(quote.initiationFee)}
          hostEarnings="-"
        />
      ) : null}

      {(breakdown.ownerDiscount ?? 0) > 0 ||
      customerManualDiscount(quote) > 0 ? (
        <DiscountLineItem
          title="Manual Discount"
          customerPrice={discountAmount(customerManualDiscount(quote))}
          hostEarnings={discountAmount(breakdown.ownerDiscount)}
        />
      ) : null}

      {(quote.salesTax ?? 0) > 0 ? (
        <SimpleLineItem
          title="Sales tax"
          customerPrice={money(quote.salesTax)}
          customerOnly
        />
      ) : null}
    </>
  );
};

const renderDailyCustomerBreakdown = (
  quote: AdminReservation,
  insuranceName?: string | null
) => {
  const carName = quote.car
    ? `${quote.car.brandName} ${quote.car.modelName}`
    : 'Rental';

  const carSubtotal = rateSubtotal(quote);
  const rate = quote.reservationRates?.[0];
  const dayCaption = quote.numDays > 0 ? `x${quote.numDays} days` : undefined;

  return (
    <>
      <RateLineItem
        title={carName}
        rateLabel={rate ? `${money(rate.dailyRate)}/day` : undefined}
        quantityLabel={dayCaption}
        customerPrice={money(carSubtotal)}
        customerOnly
      />

      {quote.insurance ? (
        (quote.insuranceCost ?? 0) > 0 && quote.numDays > 0 ? (
          <RateLineItem
            title={getInsuranceTitle(quote.insurance, insuranceName)}
            rateLabel={`${money(quote.insuranceCost / quote.numDays)}/day`}
            quantityLabel={dayCaption}
            customerPrice={money(quote.insuranceCost)}
            customerOnly
          />
        ) : (
          <SimpleLineItem
            title={getInsuranceTitle(quote.insurance, insuranceName)}
            customerPrice={money(quote.insuranceCost)}
            customerOnly
          />
        )
      ) : null}

      {(quote.extras ?? []).map((extra) => (
        <SimpleLineItem
          key={extra.id}
          title={extra.title}
          customerPrice={money(extra.amount)}
          customerOnly
        />
      ))}

      {(quote.totalDeliveryCost ?? 0) > 0 ? (
        <SimpleLineItem
          title="Delivery fee"
          customerPrice={money(quote.totalDeliveryCost)}
          customerOnly
        />
      ) : null}

      {(quote.salesTax ?? 0) > 0 ? (
        <SimpleLineItem
          title="Sales tax"
          customerPrice={money(quote.salesTax)}
          customerOnly
        />
      ) : null}

      {customerRateDiscount(quote, 'Weekly Discount') > 0 ? (
        <DiscountLineItem
          title="Weekly Discount"
          customerPrice={discountAmount(
            customerRateDiscount(quote, 'Weekly Discount')
          )}
          customerOnly
        />
      ) : null}

      {customerRateDiscount(quote, 'Monthly Discount') > 0 ? (
        <DiscountLineItem
          title="Monthly Discount"
          customerPrice={discountAmount(
            customerRateDiscount(quote, 'Monthly Discount')
          )}
          customerOnly
        />
      ) : null}

      {customerManualDiscount(quote) > 0 ? (
        <DiscountLineItem
          title="Manual Discount"
          customerPrice={discountAmount(customerManualDiscount(quote))}
          customerOnly
        />
      ) : null}

      {(quote.holdAmount ?? 0) > 0 ? (
        <SimpleLineItem
          title="Security deposit"
          customerPrice={money(quote.holdAmount)}
          customerOnly
        />
      ) : null}
    </>
  );
};

const renderSubscriptionCustomerBreakdown = (
  quote: AdminReservation,
  insuranceName?: string | null,
  pricingCaption?: string | null
) => {
  const carName = quote.car
    ? `${quote.car.brandName} ${quote.car.modelName}`
    : 'Subscription';

  return (
    <>
      <CaptionLineItem
        title={carName}
        caption={pricingCaption || undefined}
        customerPrice={money(subscriptionCustomerPrice(quote))}
        customerOnly
      />

      {quote.insurance ? (
        <SimpleLineItem
          title={getInsuranceTitle(quote.insurance, insuranceName)}
          customerPrice={money(quote.insuranceCost)}
          customerOnly
        />
      ) : null}

      {(quote.totalDeliveryCost ?? 0) > 0 ? (
        <SimpleLineItem
          title="Delivery fee"
          customerPrice={money(quote.totalDeliveryCost)}
          customerOnly
        />
      ) : null}

      {(quote.salesTax ?? 0) > 0 ? (
        <SimpleLineItem
          title="Sales tax"
          customerPrice={money(quote.salesTax)}
          customerOnly
        />
      ) : null}

      {(quote.initiationFee ?? 0) > 0 ? (
        <SimpleLineItem
          title="Initiation fee"
          customerPrice={money(quote.initiationFee)}
          customerOnly
        />
      ) : null}

      {(quote.ownerDiscount ?? 0) > 0 ? (
        <DiscountLineItem
          title="Manual Discount"
          customerPrice={discountAmount(quote.ownerDiscount)}
          customerOnly
        />
      ) : null}
    </>
  );
};

const renderBreakdown = (
  quote: AdminReservation,
  breakdown: HostEarningsBreakdown,
  insuranceName?: string | null
) =>
  isDailyHostEarningsBreakdown(breakdown)
    ? renderDailyBreakdown(quote, breakdown, insuranceName)
    : renderSubscriptionBreakdown(quote, breakdown, insuranceName);

export const SummarySidebar = ({
  glance,
  quote,
  isQuoting,
  quoteError,
  missingRequirements = [],
  insuranceName,
  reservationType,
  pricingCaption,
}: Props) => {
  const { styles } = useStyles(stylesheet);

  const glanceSections = [
    {
      label: 'Customer details',
      lines: [glance.customerName, glance.customerPhone],
    },
    {
      label: 'Pick up Location',
      lines: [
        glance.pickupDatetime
          ? format(glance.pickupDatetime, 'MMM d | h:mm a')
          : null,
        glance.pickupAddress,
      ],
    },
    {
      label: 'Return Location',
      lines: [
        glance.returnDatetime
          ? format(glance.returnDatetime, 'MMM d | h:mm a')
          : null,
        glance.returnAddress,
      ],
    },
  ].filter((section) => section.lines.some(Boolean));

  const renderPayments = () => {
    if (isQuoting) {
      return (
        <PaymentsCard>
          <View style={styles.centeredState}>
            <Loader style={{ height: 80, width: 80 }} />
          </View>
        </PaymentsCard>
      );
    }

    if (quoteError) {
      return (
        <View style={styles.card}>
          <Text type="title2">Payments</Text>
          <Text type="caption1" style={styles.errorText}>
            {quoteError}
          </Text>
        </View>
      );
    }

    if (quote?.hostEarningsBreakdown) {
      const breakdown = quote.hostEarningsBreakdown;

      return (
        <PaymentsCard>
          {renderBreakdown(quote, breakdown, insuranceName)}

          <PaymentsTotal
            customerTotal={money(quote.totalCost)}
            earningsTotal={money(breakdown.total)}
          />
        </PaymentsCard>
      );
    }

    if (quote) {
      return (
        <PaymentsCard customerOnly>
          {reservationType === 'daily'
            ? renderDailyCustomerBreakdown(quote, insuranceName)
            : renderSubscriptionCustomerBreakdown(
                quote,
                insuranceName,
                pricingCaption
              )}

          <PaymentsTotal customerTotal={money(quote.totalCost)} customerOnly />

          <Text type="caption1" style={styles.hintText}>
            Select a customer to see your earnings.
          </Text>
        </PaymentsCard>
      );
    }

    const placeholder =
      missingRequirements.length > 0
        ? `Select ${formatMissingRequirements(
            missingRequirements
          )} to see the price breakdown.`
        : 'Select dates, location, car, and insurance to see the price breakdown.';

    return (
      <View style={styles.card}>
        <Text type="title2">Payments</Text>
        <Text type="caption1" style={styles.secondaryText}>
          {placeholder}
        </Text>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.glanceCard}>
        <View style={styles.glanceHeader}>
          <Text type="title2">At A Glance</Text>
        </View>

        <View style={styles.glanceBody}>
          {glanceSections.map((section, index) => (
            <React.Fragment key={section.label}>
              {index > 0 ? <View style={styles.divider} /> : null}

              <GlanceItem label={section.label} lines={section.lines} />
            </React.Fragment>
          ))}
        </View>
      </View>

      {renderPayments()}
    </View>
  );
};

const stylesheet = createStyleSheet((theme) => ({
  container: {
    gap: 24,
  },
  card: {
    gap: 16,
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: theme.colors.card.default.borderColor,
    backgroundColor: theme.colors.card.default.backgroundColor,
  },
  glanceCard: {
    borderRadius: 16,
    borderWidth: 1,
    borderColor: theme.colors.card.default.borderColor,
    backgroundColor: theme.colors.card.default.backgroundColor,
    overflow: 'hidden',
  },
  glanceHeader: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.card.default.borderColor,
  },
  glanceBody: {
    gap: 16,
    padding: 16,
  },
  divider: {
    height: 1,
    backgroundColor: theme.colors.card.default.borderColor,
  },
  paymentsCard: {
    borderRadius: 16,
    borderWidth: 1,
    borderColor: theme.colors.card.default.borderColor,
    backgroundColor: theme.colors.card.default.backgroundColor,
    overflow: 'hidden',
  },
  paymentsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.card.default.borderColor,
  },
  paymentsBody: {
    gap: 8,
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 0,
  },
  paymentsFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginTop: 16,
    paddingTop: 16,
    paddingBottom: 16,
    borderTopWidth: 1,
    borderTopColor: theme.colors.card.default.borderColor,
  },
  paymentsTitle: {
    flexGrow: 1,
    flexShrink: 1,
    marginRight: 8,
  },
  columnLabel: {
    textAlign: 'right',
  },
  primaryText: {
    color: theme.colors.text.primary,
  },
  amountPair: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
    flexShrink: 0,
  },
  amountPairCustomerOnly: {
    justifyContent: 'flex-end',
  },
  customerColumn: {
    width: 96,
    textAlign: 'right',
  },
  earningsColumn: {
    width: 96,
    textAlign: 'right',
  },
  customerAmount: {
    width: 96,
    textAlign: 'right',
    flexShrink: 0,
    fontVariant: ['tabular-nums'],
  },
  earningsAmount: {
    width: 96,
    textAlign: 'right',
    flexShrink: 0,
    fontVariant: ['tabular-nums'],
  },
  customerAmountSlot: {
    width: 96,
    flexShrink: 0,
  },
  stackedLineItem: {
    gap: 4,
  },
  rateLineMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    width: '100%',
  },
  rateMetaLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
  },
  rateLabel: {
    flexShrink: 0,
  },
  quantityLabel: {
    flexShrink: 0,
    marginLeft: 'auto',
  },
  captionLabel: {
    flex: 1,
    minWidth: 0,
  },
  simpleLineItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  simpleLineItemTitle: {
    flex: 1,
    flexShrink: 1,
  },
  secondaryText: {
    color: theme.colors.text.secondary,
  },
  errorText: {
    color: theme.colors.input.error.helperText,
  },
  discountText: {
    color: theme.colors.chip.brand.color,
  },
  hintText: {
    color: theme.colors.text.secondary,
    paddingBottom: 16,
  },
  centeredState: {
    alignItems: 'center',
    paddingVertical: 16,
    paddingBottom: 16,
  },
}));
